import React, { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Typography,
  Switch,
  Button,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AlertModal from "../../Requirement/components/common/AlerModel";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transition: "transform 300ms ease-in-out",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#de6b14",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 22, height: 22 },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: "#ccc",
    opacity: 1,
  },
}));

const SellerFarmerPolicySecurity = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    twoFactorAuth: false,
    loginAlerts: true,
    dataSharing: false,
  });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const handleModalClose = () =>
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));

  const handleToggle = (name) => (e) =>
    setSettings((prev) => ({ ...prev, [name]: e.target.checked }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "Privacy & Security settings updated successfully.",
        autoClose: false,
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Failed to save settings.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
  }, [navigate]);

  return (
    <div className="container mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-4">
        <div className="table-header">
          <div className="header-text">
            <div className="head-text">Privacy & Security</div>
            <div className="head-subtext">
              Manage your privacy and security settings
            </div>
          </div>
          <div className="head-btn">
            <div className="submit-btn-form">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
        <div className="row g-3 mt-2">
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Two-Factor Authentication
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add an extra layer of security
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={settings.twoFactorAuth}
                    onChange={handleToggle("twoFactorAuth")}
                  />
                }
                label=""
              />
            </div>
          </div>
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Login Alerts
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Get notified of new logins
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={settings.loginAlerts}
                    onChange={handleToggle("loginAlerts")}
                  />
                }
                label=""
              />
            </div>
          </div>
          <div className="col-md-6">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Data Sharing
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Allow data sharing with partners
                </Typography>
              </div>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={settings.dataSharing}
                    onChange={handleToggle("dataSharing")}
                  />
                }
                label=""
              />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Privacy Policy
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Terms of Service</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="textSecondary">
                Our terms of service outline the rules and regulations for using
                our platform. By accessing this platform, you agree to be bound
                by these terms.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Privacy Policy</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="textSecondary">
                We are committed to protecting your privacy. This policy
                explains how we collect, use, and safeguard your personal
                information.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default SellerFarmerPolicySecurity;
